import { Flex, Icon, Box, CloseButton, Spinner, Text } from '@cad-arc';
import { UserExport } from '@mark43/cad-api';
import { getExportExpiration, getIconForExport } from '../helpers';
import { CommonSize, getOneSizeSmaller } from '../../common/helpers/utils';
import { Tooltip } from '~/common/components/tooltip';

type ExportListItemProps = {
    item: UserExport;
    onRemoveExport?: (e: UserExport) => void;
    onClickExport?: (e: UserExport) => void;
    size?: CommonSize;
};

export function ExportListItem({ item, onRemoveExport, onClickExport, size }: ExportListItemProps) {
    const isFailed = item.failedPrint;
    const isLoading = item.expirationDate === undefined && !isFailed;
    const isReady = item.file !== undefined;
    const canInteract = isReady && !isLoading && !isFailed;

    const smallerSize = getOneSizeSmaller(size);

    const handleRemoveItem = (e: React.MouseEvent | React.KeyboardEvent, item: UserExport) => {
        e.stopPropagation();
        onRemoveExport?.(item);
    };

    const content = (
        <Flex
            key={item.id}
            borderRadius="md"
            backgroundColor="surface.accent"
            w="100%"
            justifyContent="space-between"
            alignItems="center"
            _hover={
                canInteract
                    ? {
                          backgroundColor: 'var(--arc-colors-interactive-hover)',
                      }
                    : undefined
            }
            _active={
                canInteract
                    ? {
                          backgroundColor: 'var(--arc-colors-interactive-active)',
                      }
                    : undefined
            }
            cursor={canInteract ? 'pointer' : 'default'}
            data-testid={`EXPORT_ITEM_${item.id}`}
            tabIndex={canInteract ? 0 : undefined}
            role={canInteract ? 'button' : undefined}
            onClick={canInteract ? () => { onClickExport?.(item); } : undefined}
            onKeyDown={canInteract ? (e) => { if (e.key === 'Enter') { onClickExport?.(item); } } : undefined}
        >
            <Flex
                alignItems="center"
                width="100%"
                p={2}
            >
                {isLoading ? (
                    <Box w="24px" data-testid="EXPORT_ITEM_SPINNER">
                        <Spinner size={smallerSize} />
                    </Box>
                ) : (
                    <Flex
                        justifyContent="center"
                        alignItems="center"
                        data-testid={`EXPORT_ICON_${getIconForExport(item)}`}
                    >
                        <Icon icon={getIconForExport(item)} size="lg" color="icon.default" />
                    </Flex>
                )}
                <Flex direction="column" pl={isLoading ? 1 : 2} data-testid="EXPORT_ITEM_DOWNLOAD">
                    <Text
                        variant="bodyMd"
                        color={isFailed ? 'negative' : isReady ? 'info' : 'primary'}
                        noOfLines={1}
                        style={{ wordBreak: 'break-all' }}
                        fontSize={size}
                        title={item.title}
                    >
                        {item.title}
                    </Text>
                    <Text fontSize={smallerSize} color="text.secondary">
                        {isFailed
                            ? 'Error generating file'
                            : isReady
                              ? getExportExpiration(item)
                              : 'Processing...'}
                    </Text>
                </Flex>
            </Flex>
            {/* close button shows when an item is ready or if it fails so it can be removed: */}
            {(canInteract || isFailed) && (
                <Box pr={2}>
                    <CloseButton
                        aria-label="close"
                        size={size}
                        onClick={(e: React.MouseEvent) => handleRemoveItem(e, item)}
                        onKeyDown={(e: React.KeyboardEvent) => {if (e.key === 'Enter') { handleRemoveItem(e, item)}}}
                        data-testid="EXPORT_CLEAR_BUTTON"
                    />
                </Box>
            )}
        </Flex>
    );

    if (isFailed && item.message) {
        return <Tooltip overlay={item.message}>{content}</Tooltip>;
    }

    return content;
}
